/**
 * The two-level correction approval, and the honest shape of it in this build.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THERE IS NO ENDPOINT THAT LISTS CORRECTIONS.
 *
 *  `openapi.yaml` publishes `POST /attendance/corrections` and the two decision
 *  endpoints and **no `GET` on that collection at all** — not an unmounted one, the
 *  way `POST /auth/switch-company` and `GET /productivity/explain` were unmounted
 *  for Tasks 41 and 42, but none in the contract. Nothing else returns
 *  `wp_employee_explanations` rows either: `GET /audit-logs` needs
 *  `workforce.view_audit_logs`, which is in nobody's `DEFAULT_GRANTS`.
 *
 *  So this is a **decision surface keyed by id**, which is exactly what the mounted
 *  endpoints support, and the panel says so in place of a list rather than
 *  pretending to be a queue that is empty.
 *
 *  The consequence is stated on screen at severity `high` rather than buried here:
 *  an approver cannot read the request before deciding it. What they *can* read is
 *  the **day** — the calendar and the drawer above are the same employee's day with
 *  its completeness and its provenance — so the guidance points there. Shipping no
 *  control at all was the alternative considered, and it leaves Epic 2's workflow
 *  with no approval path in this build at all; shipping one with a loud, accurate
 *  warning was judged the lesser harm, and it is recorded as a gap rather than as a
 *  finished queue.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * ── The two rules this panel reflects and does not re-derive ────────────────
 *
 *  1. **Nobody decides their own explanation**, at either gate, whatever they hold —
 *     and the rule sits on the **subject**, not on the filer, so it cannot be routed
 *     around by having somebody else file it (filing for another person is refused
 *     outright as well). The client does not check it: `corrections-service.ts`
 *     answers `403 WORKFORCE_APPROVAL_NOT_ALLOWED` with
 *     `details.reason = 'self_decision'`, and that answer is rendered.
 *  2. **`requires_hr_approval` is snapshotted when the request was filed**, never
 *     read from the company's current policy. It arrives on the record and is
 *     displayed with that sentence attached; nothing here consults a config.
 *
 * ── Why the refusals are not routed through `ApiErrorNote` ──────────────────
 *
 * That component maps `WORKFORCE_NOT_FOUND` to "this build does not serve that",
 * which is right for an unmounted endpoint and wrong here: a `404` on a decision
 * means *no correction with this id exists in your company*, and the server answers
 * a missing id and another tenant's id identically **on purpose**. Reporting that as
 * a missing feature would teach the operator to go looking for a deployment problem.
 * The four codes this surface actually produces each get their own sentence.
 */

import { useCallback, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import { correctionDecisionPath } from '../../api/paths';
import { WorkforceApiError, permissionDeniedCode } from '../../api/errors';
import type { ApprovalStatus, CorrectionRecord, ExplanationReasonCode } from '../../api/types';
import { useSession } from '../../auth/SessionProvider';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { PermissionDenied } from '../../states/PermissionDenied';
import { StatusNote } from '../../states/StatusNote';
import { formatDurationMinutes } from '../../timer/elapsed';
import { ErrorCodeLine } from '../../states/ErrorCodeLine';

const APPROVAL_LABEL: Readonly<Record<ApprovalStatus, `k3.approval.${ApprovalStatus}`>> = {
  pending_manager: 'k3.approval.pending_manager',
  pending_hr: 'k3.approval.pending_hr',
  approved: 'k3.approval.approved',
  rejected: 'k3.approval.rejected',
  withdrawn: 'k3.approval.withdrawn',
};

const REASON_CODE_LABEL: Readonly<
  Record<ExplanationReasonCode, `k3.reasonCode.${ExplanationReasonCode}`>
> = {
  meeting: 'k3.reasonCode.meeting',
  phone_call: 'k3.reasonCode.phone_call',
  reading_document: 'k3.reasonCode.reading_document',
  paperwork: 'k3.reasonCode.paperwork',
  client_visit: 'k3.reasonCode.client_visit',
  break: 'k3.reasonCode.break',
  offsite_work: 'k3.reasonCode.offsite_work',
  agent_down: 'k3.reasonCode.agent_down',
  device_issue: 'k3.reasonCode.device_issue',
  power_outage: 'k3.reasonCode.power_outage',
  approved_leave: 'k3.reasonCode.approved_leave',
  other: 'k3.reasonCode.other',
};

/** `CorrectionDecisionRequest.note` — `minLength: 3` in the contract. */
const MIN_NOTE = 3;

export interface CorrectionQueueProps {
  readonly timezone: string;
  /** Bumped after a settled decision, so the grid re-reads the day it changed. */
  readonly onDecided: () => void;
}

export function CorrectionQueue({ timezone, onDecided }: CorrectionQueueProps): ReactNode {
  const { t, locale } = useI18n();
  const { client } = useSession();
  const [id, setId] = useState('');
  const [note, setNote] = useState('');
  const [approvedMinutes, setApprovedMinutes] = useState('');
  const [pending, setPending] = useState(false);
  const [record, setRecord] = useState<CorrectionRecord | null>(null);
  const [error, setError] = useState<WorkforceApiError | null>(null);

  // ── The approval is two presses ────────────────────────────────────────────
  //
  // A settled approval writes `approved_offline_seconds` into somebody's recorded
  // hours, enqueues a recalculation and cannot be taken back from this screen —
  // `WORKFORCE_INVALID_STATE_TRANSITION` is what a second attempt earns. So the
  // first press arms it and prints what is about to happen; the second sends it.
  // Rejection stays one press: it is answerable, it already carries a mandatory
  // note, and putting friction on the refusal would make refusing the slower
  // option, which is not a thing to teach an approver.
  //
  // Editing the id or the note disarms, so a confirm can never be spent on a
  // different correction from the one it was read against.
  const [arming, setArming] = useState(false);

  const decide = useCallback(
    async (decision: 'approve' | 'reject'): Promise<void> => {
      setArming(false);
      setPending(true);
      setError(null);
      setRecord(null);
      try {
        const minutes = Number(approvedMinutes);
        const body =
          decision === 'approve' && approvedMinutes.trim() !== '' && Number.isInteger(minutes)
            ? { note, approved_adjustment_minutes: minutes }
            : { note };
        const result = await client.request<CorrectionRecord>(
          correctionDecisionPath(id.trim(), decision),
          { method: 'POST', body },
        );
        setRecord(result);
        onDecided();
      } catch (cause) {
        setError(
          cause instanceof WorkforceApiError
            ? cause
            : new WorkforceApiError({
                code: 'CLIENT_TRANSPORT_FAILED',
                message: cause instanceof Error ? cause.message : 'request failed',
                status: 0,
              }),
        );
      } finally {
        setPending(false);
      }
    },
    [approvedMinutes, client, id, note, onDecided],
  );

  const ready = id.trim() !== '' && note.trim().length >= MIN_NOTE && !pending;
  const durations = { hour: t('common.hourShort'), minute: t('common.minuteShort') };

  return (
    <section className="wp-section" aria-labelledby="k3-queue-heading">
      <h2 className="wp-section__title" id="k3-queue-heading">
        {t('k3.queue.heading')}
      </h2>

      {/* Severity `high` and `role="alert"`: this is not a footnote about the build,
          it is the condition under which somebody is about to decide another
          person's hours. */}
      <StatusNote
        severity="high"
        role="alert"
        title={t('k3.queue.noList')}
        testId="k3-queue-no-list"
      >
        <p>{t('k3.queue.twoLevel')}</p>
      </StatusNote>

      <div className="wp-b-queue">
        {/* The explanation id stays a typed id and says so: there is no queue endpoint
            to list, so the honest control is a box under a line naming the two places
            the id is printed (finish review, M2). */}
        <div className="wp-field">
          <label htmlFor="k3-queue-id">{t('k3.queue.idLabel')}</label>
          <input
            id="k3-queue-id"
            name="correction_id"
            value={id}
            aria-describedby="k3-queue-id-hint"
            onChange={(event) => {
              setId(event.target.value);
              setArming(false);
            }}
            data-testid="k3-queue-id"
          />
          <p className="wp-small wp-muted" id="k3-queue-id-hint">
            {t('k3.queue.idHint')}
          </p>
        </div>

        <div className="wp-field">
          <label htmlFor="k3-queue-note">{t('k3.queue.noteLabel')}</label>
          <input
            id="k3-queue-note"
            name="note"
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
              setArming(false);
            }}
            data-testid="k3-queue-note"
          />
          <p className="wp-small wp-muted">{t('k3.queue.noteHelp')}</p>
        </div>

        <div className="wp-field">
          <label htmlFor="k3-queue-minutes">{t('k3.queue.approvedMinutes')}</label>
          <input
            id="k3-queue-minutes"
            name="approved_adjustment_minutes"
            inputMode="numeric"
            value={approvedMinutes}
            onChange={(event) => {
              setApprovedMinutes(event.target.value);
            }}
            data-testid="k3-queue-minutes"
          />
          <p className="wp-small wp-muted">{t('k3.queue.approvedMinutesHelp')}</p>
        </div>
      </div>

      {arming ? (
        <div className="wp-b-confirm" data-testid="k3-queue-confirm">
          <p className="wp-small wp-b-confirm__text">{t('k3.queue.confirmApprove.help')}</p>
          <button
            type="button"
            className="wp-button wp-button--primary"
            disabled={!ready}
            onClick={() => {
              void decide('approve');
            }}
            data-testid="k3-queue-approve-confirm"
          >
            {pending ? t('k3.queue.deciding') : t('k3.queue.confirmApprove')}
          </button>
          <button
            type="button"
            className="wp-button wp-button--quiet"
            onClick={() => {
              setArming(false);
            }}
            data-testid="k3-queue-approve-cancel"
          >
            {t('common.cancel')}
          </button>
        </div>
      ) : (
        <div className="wp-b-actions">
          <button
            type="button"
            className="wp-button wp-button--primary"
            disabled={!ready}
            onClick={() => {
              setArming(true);
            }}
            data-testid="k3-queue-approve"
          >
            {pending ? t('k3.queue.deciding') : t('k3.queue.approve')}
          </button>
          <button
            type="button"
            className="wp-button"
            disabled={!ready}
            onClick={() => {
              void decide('reject');
            }}
            data-testid="k3-queue-reject"
          >
            {pending ? t('k3.queue.deciding') : t('k3.queue.reject')}
          </button>
        </div>
      )}

      {error === null ? null : <DecisionRefusal error={error} />}

      {record === null ? null : (
        // The decided record, read back as a receipt: one ruled 44px line per fact,
        // key on the left, value on the right. Not a card — a card here would be a
        // box inside the page for a thing that is a list of lines.
        <dl className="wp-b-record" data-testid="k3-queue-record">
          <dt>{t('k3.queue.idLabel')}</dt>
          <dd>
            <code className="wp-mono">{record.id}</code>
          </dd>

          <dt>{t('k3.roster.col.status')}</dt>
          <dd data-testid="k3-queue-status">
            {t('k3.queue.decided', { status: t(APPROVAL_LABEL[record.status]) })}
          </dd>

          <dt>{t('k3.queue.requestedLabel')}</dt>
          <dd>
            {t('k3.queue.requested', {
              minutes: formatDurationMinutes(record.requested_minutes, durations),
              start: formatInstant(record.requested_start_at, locale, timezone),
              end: formatInstant(record.requested_end_at, locale, timezone),
            })}
          </dd>

          {record.approved_minutes === null ? null : (
            <>
              <dt>{t('k3.queue.approvedMinutes')}</dt>
              <dd data-testid="k3-queue-approved-minutes">
                {t('k3.queue.approvedMinutesOut', {
                  minutes: formatDurationMinutes(record.approved_minutes, durations),
                })}
              </dd>
            </>
          )}

          <dt>{t('k3.queue.reasonLabel')}</dt>
          <dd>
            {t('k3.queue.reasonCode', { reason: t(REASON_CODE_LABEL[record.reason_code]) })}
            <span className="wp-small wp-muted">
              {' · '}
              {record.reason_note === null
                ? t('k3.queue.noNote')
                : t('k3.queue.note', { note: record.reason_note })}
              {record.evidence_keys.length === 0
                ? null
                : ` · ${t('k3.queue.evidence', { count: record.evidence_keys.length })}`}
            </span>
          </dd>

          <dt>{t('k3.queue.hrLabel')}</dt>
          <dd data-testid="k3-queue-requires-hr">
            {record.requires_hr_approval
              ? t('k3.queue.requiresHr.true')
              : t('k3.queue.requiresHr.false')}
          </dd>

          {record.decided_at === null ? null : (
            <>
              <dt>{t('k3.queue.decidedAtLabel')}</dt>
              <dd className="wp-small wp-muted">
                {t('k3.queue.decidedAt', {
                  when: formatInstant(record.decided_at, locale, timezone),
                })}
              </dd>
            </>
          )}
        </dl>
      )}
    </section>
  );
}

/**
 * The four refusals this surface produces, each with its own sentence.
 *
 * Ordered by how specific they are, not by status code: `self_decision` is a
 * `403` that a `403` handler would otherwise swallow into "you lack a permission",
 * and it is not about a permission at all.
 */
function DecisionRefusal({ error }: { readonly error: WorkforceApiError }): ReactNode {
  const t = useI18n().t;

  if (error.code === 'WORKFORCE_APPROVAL_NOT_ALLOWED') {
    return (
      <StatusNote
        severity="high"
        role="alert"
        title={t('k3.queue.selfDecision')}
        testId="k3-queue-self-decision"
      >
        <ErrorTrailer error={error} />
      </StatusNote>
    );
  }

  if (error.code === 'WORKFORCE_SCOPE_DENIED') {
    return (
      <StatusNote
        severity="high"
        role="alert"
        title={t('k3.queue.outOfScope')}
        testId="k3-queue-out-of-scope"
      >
        <ErrorTrailer error={error} />
      </StatusNote>
    );
  }

  if (error.code === 'WORKFORCE_NOT_FOUND') {
    return (
      <StatusNote
        severity="medium"
        role="alert"
        title={t('k3.queue.notFound')}
        testId="k3-queue-not-found"
      >
        <ErrorTrailer error={error} />
      </StatusNote>
    );
  }

  if (error.code === 'WORKFORCE_INVALID_STATE_TRANSITION') {
    const allowed = error.details.allowed_transitions;
    return (
      <StatusNote
        severity="medium"
        role="alert"
        title={
          Array.isArray(allowed) && allowed.length > 0
            ? t('k3.queue.invalidState', { allowed: allowed.map(String).join(', ') })
            : t('k3.queue.invalidStateNoList')
        }
        testId="k3-queue-invalid-state"
      >
        <ErrorTrailer error={error} />
      </StatusNote>
    );
  }

  // The remaining chain refusals — a session with no `manage_shifts` that reached
  // this panel by other means — keep the shared component and its no-retry rule.
  const denied = permissionDeniedCode(error);
  if (denied !== null) {
    return (
      <PermissionDenied
        code={denied}
        correlationId={error.correlationId}
        testId="k3-queue-denied"
      />
    );
  }

  return <ApiErrorNote error={error} subject={t('k3.queue.subject')} testId="k3-queue-error" />;
}

/** The reference id on screen in every branch, with the machine code behind it. */
function ErrorTrailer({ error }: { readonly error: WorkforceApiError }): ReactNode {
  return <ErrorCodeLine code={error.code} correlationId={error.correlationId} />;
}
